import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox,
  Chip, FormControlLabel, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { tenantModules } from '../../config/modules';
import { TableColumnEditor } from '../common/TableColumnEditor';
import { useAuth } from '../../contexts/AuthContext';
import { useModules } from '../../contexts/ModuleContext';
import { settingsAPI } from '../../services/api';
import { brandColors, brandCss, RADIUS } from '../../theme/appTheme';
import {
  getDefaultFilterFields,
  getDefaultVisibleFields,
  getFilterableFieldOptions,
  sanitizeFilterFields,
  sanitizeVisibleFields,
} from '../../utils/tableColumnSettings';

export const TableColumnsSettings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { isModuleEnabled } = useModules();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [filterDrafts, setFilterDrafts] = useState<Record<string, string[]>>({});

  const tableModules = useMemo(
    () => tenantModules.filter((m) => m.columns.length > 1 && isModuleEnabled(m.key)),
    [isModuleEnabled],
  );

  const saveModule = async (
    moduleKey: string,
    patch: { columns?: string[]; filters?: string[] },
    allCols: typeof tableModules[0]['columns'],
    filterOptions: { field: string }[],
  ) => {
    try {
      setSavingKey(moduleKey);
      setError('');
      const prev = (user?.company?.tableSettings?.[moduleKey] || {}) as { columns?: string[]; filters?: string[] };
      const columns = patch.columns !== undefined
        ? sanitizeVisibleFields(allCols, patch.columns)
        : (prev.columns?.length ? sanitizeVisibleFields(allCols, prev.columns) : getDefaultVisibleFields(allCols));
      const filters = patch.filters !== undefined
        ? sanitizeFilterFields(filterOptions, patch.filters)
        : sanitizeFilterFields(
          filterOptions,
          prev.filters ?? getDefaultFilterFields(moduleKey),
        );
      const tableSettings = {
        ...(user?.company?.tableSettings || {}),
        [moduleKey]: { columns, filters },
      };
      await settingsAPI.update({ tableSettings });
      await refreshUser();
      setMessage(`${tableModules.find((m) => m.key === moduleKey)?.title || moduleKey} table settings saved`);
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Failed to save table settings');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <Box>
      <Box sx={{
        mb: 2.5, p: 2, borderRadius: 3,
        background: `linear-gradient(135deg, #0f172a 0%, #1e293b 45%, ${brandCss.primary} 160%)`,
        color: '#fff', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.15)',
          }}>
            <TableChartIcon />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={15}>Data Tables</Typography>
            <Typography fontSize={12} sx={{ opacity: 0.85 }}>
              Choose visible columns and which fields appear as filter dropdowns on each list
            </Typography>
          </Box>
        </Box>
      </Box>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
        Pick any module field to show as a filter dropdown. Status stays as its own filter on the table.
      </Alert>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {tableModules.map((mod) => {
        const saved = user?.company?.tableSettings?.[mod.key];
        const defaultFields = getDefaultVisibleFields(mod.columns);
        const activeFields = saved?.columns?.length
          ? sanitizeVisibleFields(mod.columns, saved.columns)
          : defaultFields;
        const filterOptions = getFilterableFieldOptions(mod.columns, mod.fields || []);
        const defaultFilters = sanitizeFilterFields(filterOptions, getDefaultFilterFields(mod.key));
        const savedFilters = saved?.filters
          ? sanitizeFilterFields(filterOptions, saved.filters)
          : defaultFilters;
        const draftFilters = filterDrafts[mod.key] ?? savedFilters;
        const isCustomCols = Boolean(saved?.columns?.length && JSON.stringify(saved.columns) !== JSON.stringify(defaultFields));
        const isCustomFilters = JSON.stringify(draftFilters) !== JSON.stringify(defaultFilters);

        return (
          <Accordion
            key={mod.key}
            disableGutters
            sx={{
              mb: 1, borderRadius: '12px !important', border: `1px solid ${brandColors.border}`,
              '&:before': { display: 'none' }, overflow: 'hidden',
              boxShadow: 'none',
              '&.Mui-expanded': { boxShadow: '0 8px 24px rgba(15,23,42,0.08)' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: (isCustomCols || isCustomFilters) ? brandCss.green50 : '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, pr: 1, flexWrap: 'wrap' }}>
                <ViewColumnIcon sx={{ color: brandCss.primary, fontSize: 20 }} />
                <Typography fontWeight={700} fontSize={13}>{mod.title}</Typography>
                <Chip label={`${activeFields.length} columns`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
                <Chip
                  icon={<FilterListIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${draftFilters.length} filters`}
                  size="small"
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, bgcolor: '#fafbfc' }}>
              <Typography fontWeight={800} fontSize={13} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ViewColumnIcon sx={{ fontSize: 18, color: brandCss.primary }} />
                Columns
              </Typography>
              <TableColumnEditor
                allColumns={mod.columns}
                visibleFields={activeFields}
                defaultFields={defaultFields}
                onSave={(fields) => saveModule(mod.key, { columns: fields }, mod.columns, filterOptions)}
                onReset={() => saveModule(mod.key, { columns: defaultFields }, mod.columns, filterOptions)}
                saving={savingKey === mod.key}
              />

              <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${brandColors.border}` }}>
                <Typography fontWeight={800} fontSize={13} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <FilterListIcon sx={{ fontSize: 18, color: brandCss.primary }} />
                  Filter dropdowns
                </Typography>
                <Typography fontSize={12} color="text.secondary" sx={{ mb: 1.25 }}>
                  Select fields from this module to show as filters above the table (max 5).
                </Typography>
                {filterOptions.length === 0 ? (
                  <Typography fontSize={12} color="text.secondary">No filterable fields for this module.</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.25 }}>
                    {filterOptions.map((opt) => {
                      const checked = draftFilters.includes(opt.field);
                      return (
                        <FormControlLabel
                          key={opt.field}
                          control={(
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={!checked && draftFilters.length >= 5}
                              onChange={(_, on) => {
                                setFilterDrafts((prev) => {
                                  const current = prev[mod.key] ?? savedFilters;
                                  const next = on
                                    ? [...current, opt.field].slice(0, 5)
                                    : current.filter((f) => f !== opt.field);
                                  return { ...prev, [mod.key]: next };
                                });
                              }}
                            />
                          )}
                          label={<Typography fontSize={12.5} fontWeight={600}>{opt.header}</Typography>}
                          sx={{ m: 0, mr: 1 }}
                        />
                      );
                    })}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    disabled={savingKey === mod.key || JSON.stringify(draftFilters) === JSON.stringify(savedFilters)}
                    onClick={() => saveModule(mod.key, { filters: draftFilters }, mod.columns, filterOptions)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px` }}
                  >
                    Save filters
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    disabled={savingKey === mod.key}
                    onClick={() => {
                      setFilterDrafts((prev) => ({ ...prev, [mod.key]: defaultFilters }));
                      saveModule(mod.key, { filters: defaultFilters }, mod.columns, filterOptions);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`, borderColor: brandColors.border }}
                  >
                    Reset filters
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
